import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { trackHomePageNavigation, trackTrendingSearch } from 'lib/Tracking';
import { LARGE, MEDIUM } from 'lib/DeviceScreen';
import { useInView } from 'react-intersection-observer';
import ScrollingCarousel from 'components/Home/ScrollingCarousel';
// assets
import { screen } from 'components/styles/variables';
import trendingTrucks from 'public/static/images/home/trending-trucks.jpg';
import trendingSedans from 'public/static/images/home/trending-sedans.jpg';
import trendingSedansSmall from 'public/static/images/home/trending-sedans-small.jpg';
import trendingFamily from 'public/static/images/home/trending-family.jpg';
import trendingFamilySmall from 'public/static/images/home/trending-family-small.jpg';
import trendingCoupes from 'public/static/images/home/trending-coupes.jpg';
import Heading from 'components/Home/Heading';
import useAd from 'lib/hooks/useAd';

interface ListItemProps {
  type: string;
  image: string;
  url: string;
  smallImage: string;
}

const ListItem = ({ type, image, url, smallImage }: ListItemProps): JSX.Element => {
  const { t } = useTranslation();
  const label = t(`home.trending.${type}`);
  const [viewedRef, viewed] = useInView({ triggerOnce: true });

  return (
    <li className={`trending-searches-list-item ${type}`} ref={viewedRef}>
      <a
        href={url}
        data-trending-search-item={type}
        className="list-item-content"
        onClick={(): void => {
          trackHomePageNavigation();
          trackTrendingSearch(type);
        }}
      >
        <div className="list-item-tint" />
        <div
          role="img"
          aria-label={label}
          className={classnames('hidden-xs trending-image trending-image-lg', {
            'trending-image-lg-viewed': viewed,
          })}
        />
        <div
          role="img"
          aria-label={label}
          className={classnames('visible-xs trending-image trending-image-sm', {
            'trending-image-sm-viewed': viewed,
          })}
        />
        <div className="list-item-label">{label}</div>
      </a>
      <style jsx global>{`
        @media (min-width: ${screen.smMin}) and (max-width: ${screen.smMax}) {
          .trending-searches .trending-searches-list-item {
            height: 237px;
          }
        }

        @media (min-width: ${screen.mdMin}) and (max-width: ${screen.mdMax}) {
          .trending-searches .trending-searches-list-item {
            height: 202px;
          }
        }

        @media (min-width: ${screen.lgMin}) {
          .trending-searches .trending-searches-list-item {
            height: 377px;
          }

          .trending-searches .with-sidebar .trending-searches-list-item {
            height: 270px;
          }
        }
      `}</style>
      <style jsx>{`
        .trending-searches-list-item {
          float: left;
          padding: 1px;
          cursor: pointer;
        }

        .trending-image {
          height: 380px;
          background-repeat: no-repeat;
          background-size: contain;
        }

        .trending-image-lg {
          width: auto;
        }

        .trending-image-lg-viewed {
          background-image: url(${image});
        }

        .trending-image-sm {
          width: 300px;
        }

        .trending-image-sm-viewed {
          background-image: url(${smallImage || image});
        }

        @media (min-width: ${screen.smMin}) {
          .trending-searches-list-item.pickup,
          .trending-searches-list-item.coupe {
            width: 33%;
          }

          .trending-searches-list-item.sedan,
          .trending-searches-list-item.family {
            width: 66%;
          }
        }

        .list-item-content {
          position: relative;
          display: block;
          width: 100%;
          height: 100%;
          padding: 0;
          border: 0;
          overflow: hidden;
        }

        .list-item-tint {
          background-color: rgba(0, 0, 0, 0.25);
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
        }

        .trending-searches-list-item:hover .list-item-tint {
          background-color: rgba(0, 0, 0, 0.1);
        }

        .list-item-label {
          font-weight: 100;
          font-size: 26px;
          line-height: 26px;
          color: white;
          position: absolute;
          left: 20px;
          top: 20px;
          width: 175px;
          text-align: left;
        }
      `}</style>
    </li>
  );
};

const TrendingSearches = (): JSX.Element => {
  const { t } = useTranslation();
  const trendingSearches = [
    {
      key: 'pickup',
      type: 'pickup',
      image: trendingTrucks,
      url: '/pickup+truck-under+10000',
    },
    {
      key: 'sedan',
      type: 'sedan',
      image: trendingSedans,
      smallImage: trendingSedansSmall,
      url: '/sedan-under+5000',
    },
    {
      key: 'family',
      type: 'family',
      image: trendingFamily,
      smallImage: trendingFamilySmall,
      url: '/family-under+10000',
    },
    {
      key: 'coupe',
      type: 'coupe',
      image: trendingCoupes,
      url: '/coupe-under+15000',
    },
  ];

  const { elementId: topAdElementId, isShown: topAdIsShown } = useAd({
    dimensions: [[300, 250]],
    targeting: { inLine: 2 },
    screenSizes: [LARGE, MEDIUM],
  });

  return (
    <div className="trending-searches">
      <div className="container">
        <Heading title={t('home.trending.title')} />
        <div className="row">
          <div
            data-testid="searches-container"
            className={`searches-container ${
              topAdIsShown ? 'with-sidebar' : ''
            } col-lg-12 col-md-12 col-sm-12 col-xs-12`}
          >
            <div className="searches-list">
              <ScrollingCarousel items={trendingSearches} ItemComponent={ListItem} />
            </div>
            <div className="ad-sidebar">
              <div id={topAdElementId} className="medium-rectangle hidden-sm hidden-xs" />
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .trending-searches {
          margin-bottom: 30px;
        }

        .searches-container {
          padding: 0;
        }

        .small-rectangle {
          width: 300px;
          min-height: 120px;
        }

        .medium-rectangle {
          width: 300px;
          min-height: 250px;
        }

        @media (min-width: ${screen.smMin}) {
          .searches-container {
            padding: 0 15px;
          }
        }

        @media (min-width: ${screen.smMin}) {
          .searches-container {
            padding: 0 15px;
          }

          .searches-container.with-sidebar .ad-sidebar {
            display: none;
          }
        }

        @media (min-width: ${screen.mdMin}) {
          .searches-container.with-sidebar .ad-sidebar {
            display: block;
            width: 300px;
            float: right;
          }
        }

        @media (min-width: ${screen.lgMin}) {
          .searches-container.with-sidebar .searches-list {
            width: 834px;
            float: left;
          }
        }

        @media (min-width: ${screen.mdMin}) and (max-width: ${screen.mdMax}) {
          .searches-container.with-sidebar .searches-list {
            width: 634px;
            float: left;
          }
        }

        .home-scrolling-carousel-list {
          list-style: none;
          padding: 0;
          margin: 0;
          width: 9999px;
        }

        @media (min-width: ${screen.smMin}) {
          .home-scrolling-carousel-list {
            width: auto;
          }
        }

        @media (min-width: ${screen.smMin}) {
          .scrolling-carousel {
            overflow-x: visible;
            overflow-y: visible;
          }
        }

        .hspacer-5px {
          display: block;
          height: 5px;
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default TrendingSearches;
